import ToggleButton from "../general/toggleButton";
import {
  iteratablePricingOptions,
  personalPricingOptions,
  toggleValuePremium,
  toggleValueUltimate,
  DISABLE_MONTHLY_ULTIMATE,
} from "src/data/variables";
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { priceVariantAtom } from "src/atoms/pricingPage";

export interface SubscriptionCardsProps {
  isSeasonalDiscountActive: boolean;
}

const SubscriptionCards = ({ isSeasonalDiscountActive }: SubscriptionCardsProps) => {
  const [priceVariant, setPriceVariant] = useAtom(priceVariantAtom);
  const [lastSelectedCard, setLastSelectedCard] = useState(priceVariant);
  const [ultimatePrice, setUltimatePrice] = useState<string | undefined>(
    isSeasonalDiscountActive
      ? personalPricingOptions.ultimateYear.cardPrice
      : personalPricingOptions.ultimateYear.seasonalDiscountPrice,
  );
  const [premiumPrice, setPremiumPrice] = useState<string | undefined>(
    isSeasonalDiscountActive
      ? personalPricingOptions.premiumYear.cardPrice
      : personalPricingOptions.premiumYear.seasonalDiscountPrice,
  );

  useEffect(() => {
    if (priceVariant) {
      setLastSelectedCard(priceVariant);
      setUltimatePrice(
        priceVariant && priceVariant.startsWith("ULTIMATE")
          ? isSeasonalDiscountActive
            ? iteratablePricingOptions.find((x) => x.priceVariant === priceVariant)
                ?.seasonalDiscountPrice
            : iteratablePricingOptions.find((x) => x.priceVariant === priceVariant)?.cardPrice
          : isSeasonalDiscountActive
            ? personalPricingOptions.ultimateYear.seasonalDiscountPrice
            : personalPricingOptions.ultimateYear.cardPrice,
      );
      setPremiumPrice(
        priceVariant && !priceVariant.startsWith("ULTIMATE")
          ? isSeasonalDiscountActive
            ? iteratablePricingOptions.find((x) => x.priceVariant === priceVariant)
                ?.seasonalDiscountPrice
            : iteratablePricingOptions.find((x) => x.priceVariant === priceVariant)?.cardPrice
          : isSeasonalDiscountActive
            ? personalPricingOptions.premiumYear.seasonalDiscountPrice
            : personalPricingOptions.premiumYear.cardPrice,
      );
    }
  }, [priceVariant]);

  const handlePremiumToggleChange = (value: string) => {
    switch (value) {
      case personalPricingOptions.premiumYear.priceVariant:
        setLastSelectedCard(personalPricingOptions.premiumYear.priceVariant);

        setPremiumPrice(
          isSeasonalDiscountActive
            ? personalPricingOptions.premiumYear.seasonalDiscountPrice
            : personalPricingOptions.premiumYear.cardPrice,
        );

        setPriceVariant(personalPricingOptions.premiumYear.priceVariant);

        break;
      case personalPricingOptions.premiumLifetime.priceVariant:
        setLastSelectedCard(personalPricingOptions.premiumLifetime.priceVariant);

        setPremiumPrice(
          isSeasonalDiscountActive
            ? personalPricingOptions.premiumLifetime.seasonalDiscountPrice
            : personalPricingOptions.premiumLifetime.cardPrice,
        );

        setPriceVariant(personalPricingOptions.premiumLifetime.priceVariant);
        break;
    }
  };

  const handleUltimateToggleChange = (value: string) => {
    switch (value) {
      case personalPricingOptions.ultimateMonth.priceVariant:
        setLastSelectedCard(personalPricingOptions.ultimateMonth.priceVariant);

        setUltimatePrice(
          isSeasonalDiscountActive
            ? personalPricingOptions.ultimateMonth.seasonalDiscountPrice
            : personalPricingOptions.ultimateMonth.cardPrice,
        );

        setPriceVariant(personalPricingOptions.ultimateMonth.priceVariant);
        break;
      case personalPricingOptions.ultimateYear.priceVariant:
        setLastSelectedCard(personalPricingOptions.ultimateYear.priceVariant);

        setUltimatePrice(
          isSeasonalDiscountActive
            ? personalPricingOptions.ultimateYear.seasonalDiscountPrice
            : personalPricingOptions.ultimateYear.cardPrice,
        );

        setPriceVariant(personalPricingOptions.ultimateYear.priceVariant);
        break;
      case personalPricingOptions.ultimateLifetime.priceVariant:
        setLastSelectedCard(personalPricingOptions.ultimateLifetime.priceVariant);

        setUltimatePrice(
          isSeasonalDiscountActive
            ? personalPricingOptions.ultimateLifetime.seasonalDiscountPrice
            : personalPricingOptions.ultimateLifetime.cardPrice,
        );

        setPriceVariant(personalPricingOptions.ultimateLifetime.priceVariant);
        break;
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-x-16">
      <div
        className={`w-[280px] h-[220px] outline outline-[2.3px] rounded-lg hover:outline-brand-blue ${!lastSelectedCard.startsWith("ULTIMATE") ? "outline-brand-blue" : "outline-gray-700"} cursor-pointer`}
      >
        <p className="ml-[2.0rem] text-[1.8rem] mt-8 font-bold">Premium</p>
        <div className="flex mt-6">
          <ToggleButton
            DefaultValue={priceVariant ?? personalPricingOptions.premiumYear.priceVariant}
            isTransparent={false}
            OnValueChange={handlePremiumToggleChange}
            ToggleValues={toggleValuePremium}
            buttonColor=""
          ></ToggleButton>
        </div>
        <p className="ml-[2.1rem] text-2xl mt-5 font-bold">
          {premiumPrice}{" "}
          <span className="text-sm">
            {personalPricingOptions.premiumLifetime.seasonalDiscountPrice === premiumPrice ||
            personalPricingOptions.premiumLifetime.cardPrice === premiumPrice
              ? personalPricingOptions.premiumLifetime.priceDecorator
              : personalPricingOptions.premiumYear.priceDecorator}
          </span>
        </p>
        <p className="ml-[2.1rem] text-sm ">
          {premiumPrice === personalPricingOptions.premiumYear.seasonalDiscountPrice ||
          premiumPrice === personalPricingOptions.premiumYear.cardPrice
            ? `Billed at ${isSeasonalDiscountActive ? personalPricingOptions.premiumYear.seasonalDiscountFullPrice : personalPricingOptions.premiumYear.fullPrice} every 12 months`
            : ""}
        </p>
      </div>
      <div
        className={`w-[280px] mt-10 sm:mt-0 ${DISABLE_MONTHLY_ULTIMATE ? "sm:w-[280px]" : "sm:w-[380px]"} h-[220px] outline outline-[2.3px] rounded-lg hover:outline-brand-blue ${lastSelectedCard.startsWith("ULTIMATE") ? "outline-brand-blue" : "outline-gray-700"} cursor-pointer`}
      >
        <p className="ml-[2.0rem] text-[1.8rem] mt-8 font-bold">Ultimate</p>
        <div className="flex mt-6 w-[280px] sm:w-full">
          <ToggleButton
            key={"ULTIMATE"}
            DefaultValue={priceVariant ?? personalPricingOptions.ultimateYear.priceVariant}
            isTransparent={false}
            OnValueChange={handleUltimateToggleChange}
            ToggleValues={toggleValueUltimate}
            buttonColor=""
          ></ToggleButton>
        </div>
        <p className="ml-[2.1rem] text-2xl mt-5 font-bold">
          {ultimatePrice}{" "}
          <span className="text-sm">
            {personalPricingOptions.ultimateLifetime.seasonalDiscountPrice === ultimatePrice ||
            personalPricingOptions.ultimateLifetime.cardPrice === ultimatePrice
              ? personalPricingOptions.ultimateLifetime.priceDecorator
              : personalPricingOptions.ultimateYear.priceDecorator}
          </span>
        </p>
        <p className="ml-[2.1rem] text-sm ">
          {ultimatePrice === personalPricingOptions.ultimateYear.seasonalDiscountPrice ||
          ultimatePrice === personalPricingOptions.ultimateYear.cardPrice
            ? `Billed at ${isSeasonalDiscountActive ? personalPricingOptions.ultimateYear.seasonalDiscountFullPrice : personalPricingOptions.ultimateYear.fullPrice} every 12 months`
            : ""}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionCards;
